/**
 * Contract-Driven Autonomy (CDA) — shared type model.
 *
 * Single source of truth for the CDA `mcp:` block + per-parameter x-fields as
 * declared on a tool. Consumed by BOTH the author surface (system-editor, which
 * edits a tool's config) and the operator surface (mcp-admin, which reads a
 * tool's published Tier-3 schema). See TOOL_REF_CDA_AND_GROUNDING.md (TOOL-14..23).
 *
 * Vocabulary note: two risk vocabularies exist in the platform.
 *  - Authoring shorthand (TOOL-14): `read | write | high`.
 *  - Gate Planner enum (empowernow_common.mcp.cda.constants.RiskLevel):
 *    `read | low | medium | high | critical`.
 * `parseRiskLevel` mirrors the planner exactly (case-insensitive, unknown -> null),
 * so `"write"` is NOT a gating level — it is shown on the inherent-risk channel only.
 */

// =============================================================================
// Risk + impact
// =============================================================================

/** Gating risk levels — mirrors empowernow_common RiskLevel enum. */
export type CdaRiskLevel = 'read' | 'low' | 'medium' | 'high' | 'critical';

/** Mutation impact families (CDA x-impact.type). */
export type CdaImpactType = 'read' | 'create' | 'update' | 'delete' | 'create_or_update';

export interface CdaImpact {
  type?: CdaImpactType;
  description?: string;
}

/** Batch-consent scope (TOOL-22, x-confirm-scope). */
export interface CdaConfirmScope {
  batchable?: boolean;
  grant?: 'per-call' | 'per-tool-list' | 'per-tool';
  /** Set true when x-impact.type === 'delete' (destructive variant). */
  'x-destructive'?: boolean;
  scope_fields?: string[];
  ephemeral_fields?: string[];
  required_amr?: string[];
  allow_per_tool_grant?: boolean;
  max_candidates?: number;
  max_operations?: number;
  ttl_seconds?: number;
  delegation_binding?: string;
  candidate_resolver?: Record<string, unknown>;
}

// =============================================================================
// Parameters
// =============================================================================

/** A single input parameter with its CDA x-field annotations. */
export interface CdaParam {
  name: string;
  type?: string;
  required?: boolean;
  description?: string;
  'x-default'?: unknown;
  default?: unknown;
  'x-normalize'?: string;
  'x-redact'?: boolean;
  'x-elicit'?: string;
  'x-suggest'?: unknown;
  'x-compose'?: string;
}

// =============================================================================
// Contract
// =============================================================================

/**
 * The CDA posture declared on one tool. A faithful, UI-friendly projection of
 * the tool's `mcp:` block plus its input-schema parameters.
 */
export interface CdaContract {
  toolName: string;
  /** Marks the tool as requiring human confirmation (TOOL-14). */
  'x-cda'?: boolean;
  /** Raw declared risk (may be `read|write|high` shorthand or the full enum). */
  'x-risk'?: string;
  'x-impact'?: CdaImpact;
  'x-confirm-message'?: string;
  'x-confirm-scope'?: CdaConfirmScope;
  'x-discloses-value'?: boolean;
  'x-evidence-source'?: Record<string, unknown>;
  'x-operation-ref'?: string;
  'x-delegation'?: unknown;
  params: CdaParam[];
}

// =============================================================================
// Risk parsing — mirrors empowernow_common.mcp.cda.constants.RiskLevel
// =============================================================================

const GATING_RISK_LEVELS: readonly CdaRiskLevel[] = ['read', 'low', 'medium', 'high', 'critical'];

const RISK_ORDER: Record<CdaRiskLevel, number> = {
  read: 0,
  low: 1,
  medium: 2,
  high: 3,
  critical: 4,
};

/** Parse a raw risk string into a gating level. Unknown values (e.g. "write") -> null. */
export function parseRiskLevel(value?: string | null): CdaRiskLevel | null {
  if (!value) return null;
  const normalized = value.toLowerCase().trim();
  return (GATING_RISK_LEVELS as readonly string[]).includes(normalized)
    ? (normalized as CdaRiskLevel)
    : null;
}

/** MEDIUM, HIGH, CRITICAL require user confirmation (mirrors planner). */
export function riskRequiresConfirmation(level: CdaRiskLevel | null): boolean {
  return level === 'medium' || level === 'high' || level === 'critical';
}

/** CRITICAL requires the WAITING / enterprise-approval protocol. */
export function riskRequiresWaiting(level: CdaRiskLevel | null): boolean {
  return level === 'critical';
}

export function riskRank(level: CdaRiskLevel | null): number {
  return level ? RISK_ORDER[level] : -1;
}

// =============================================================================
// Posture helpers
// =============================================================================

const MUTATING_IMPACTS: ReadonlySet<string> = new Set([
  'create',
  'update',
  'delete',
  'create_or_update',
]);

/** Whether the impact family is destructive/irreversible (delete). */
export function isDestructive(contract: CdaContract): boolean {
  return (
    contract['x-impact']?.type === 'delete' ||
    contract['x-confirm-scope']?.['x-destructive'] === true
  );
}

/** Whether the contract describes a mutating (non-read) operation. */
export function isMutating(contract: CdaContract): boolean {
  const impact = contract['x-impact']?.type;
  if (impact && MUTATING_IMPACTS.has(impact)) return true;
  if (impact === 'read') return false;
  const raw = (contract['x-risk'] || '').toLowerCase().trim();
  if (raw === 'write' || raw === 'high' || raw === 'critical' || raw === 'medium') return true;
  return contract['x-cda'] === true;
}

/** A clearly read-only tool: declares read impact/risk and is not mutating. */
export function isReadOnly(contract: CdaContract): boolean {
  if (isMutating(contract)) return false;
  const impact = contract['x-impact']?.type;
  const raw = (contract['x-risk'] || '').toLowerCase().trim();
  return impact === 'read' || raw === 'read';
}

/**
 * Does this tool carry a CDA governance contract? A mutating tool without a
 * contract is a governance gap, not a calm empty state.
 */
export function hasContract(contract: CdaContract): boolean {
  return (
    contract['x-cda'] === true ||
    !!contract['x-confirm-message'] ||
    !!contract['x-confirm-scope'] ||
    contract['x-impact']?.type !== undefined
  );
}

/**
 * Detect a governance gap. `mutatingBinding` lets the operator surface assert a
 * mutation from an authoritative source (a write/high operation binding) even
 * when the contract block itself is absent.
 */
export function detectGovernanceGap(
  contract: CdaContract,
  opts?: { mutatingBinding?: boolean },
): boolean {
  const mutating = isMutating(contract) || opts?.mutatingBinding === true;
  return mutating && !hasContract(contract);
}

// =============================================================================
// Governance state (one primary signal — no badge soup)
// =============================================================================

export type GovernanceState =
  /** Read-only — no governance needed (calm, expected). */
  | 'read-only'
  /** Mutating, no contract — a FINDING. */
  | 'gap'
  /** Contract present, minimal coverage (declared but no confirmation/coverage). */
  | 'declared'
  /** Contract with confirmation / receipted coverage. */
  | 'governed'
  /** Strong coverage: step-up MFA, destructive lock, or bounded batch consent. */
  | 'governed-strong'
  /** Insufficient information to classify. */
  | 'unknown';

export interface GovernanceStateInfo {
  state: GovernanceState;
  /** Short label for the single primary chip. */
  label: string;
  /** One-line plain-language summary for tooltip/aria. */
  summary: string;
  /** Inherent-risk channel level (red is legitimate here). */
  risk: CdaRiskLevel | null;
  /** Whether the inherent risk is destructive. */
  destructive: boolean;
}

/**
 * Derive the single governance state for a tool. This is the basis for the one
 * primary `GovernanceSignal` — everything else is detail in the viewer.
 */
export function deriveGovernanceState(
  contract: CdaContract,
  opts?: { mutatingBinding?: boolean },
): GovernanceStateInfo {
  const risk = parseRiskLevel(contract['x-risk']);
  const destructive = isDestructive(contract);

  if (detectGovernanceGap(contract, opts)) {
    return {
      state: 'gap',
      label: 'Governance gap',
      summary: 'Mutating tool with no Contract-Driven Autonomy contract. No confirmation or receipt coverage is declared.',
      risk,
      destructive,
    };
  }

  if (isReadOnly(contract) && !hasContract(contract)) {
    return {
      state: 'read-only',
      label: 'Read-only',
      summary: 'Read-only tool — no mutation governance needed.',
      risk: risk ?? 'read',
      destructive: false,
    };
  }

  const scope = contract['x-confirm-scope'];
  const hasStepUp = !!scope?.required_amr && scope.required_amr.length > 0;
  const hasBoundedBatch = scope?.batchable === true && scope.grant === 'per-tool-list';
  const hasConfirmation = !!contract['x-confirm-message'] || riskRequiresConfirmation(risk);

  if (destructive || hasStepUp || hasBoundedBatch || riskRequiresWaiting(risk)) {
    return {
      state: 'governed-strong',
      label: 'Strongly governed',
      summary: destructive
        ? 'Destructive operation under strong controls: per-tool-list consent, step-up MFA, and signed receipts.'
        : 'Strong controls: step-up MFA and/or bounded batch consent, with signed receipts on every call.',
      risk,
      destructive,
    };
  }

  if (hasConfirmation || contract['x-cda'] === true) {
    return {
      state: 'governed',
      label: 'Governed',
      summary: 'Requires human confirmation and is authorized live by the PDP, with a signed receipt on every call.',
      risk,
      destructive,
    };
  }

  if (hasContract(contract)) {
    return {
      state: 'declared',
      label: 'Contract declared',
      summary: 'A CDA contract is declared. Confirmation coverage is minimal — review before relying on it.',
      risk,
      destructive,
    };
  }

  return {
    state: 'unknown',
    label: 'Unknown',
    summary: 'Governance posture could not be determined from the available metadata.',
    risk,
    destructive,
  };
}

// =============================================================================
// Parsing a tool schema into a CdaContract
// =============================================================================

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Build a CdaContract from a raw tool definition. Handles both `mcp` and
 * `metadata` blocks and both `inputSchema` / `input_schema` naming, mirroring
 * the Gate Planner's extraction.
 */
export function parseCdaContract(toolSchema: Record<string, unknown> | null | undefined): CdaContract {
  const schema = toolSchema || {};
  const toolName = typeof schema.name === 'string' ? schema.name : 'tool';

  // Risk metadata can live under either `mcp` or `metadata`.
  const mcp = asRecord(schema.mcp) || asRecord(schema.metadata) || {};

  const inputSchema =
    asRecord(schema.inputSchema) || asRecord(schema.input_schema) || {};
  const properties = asRecord(inputSchema.properties) || {};
  const requiredList = Array.isArray(inputSchema.required)
    ? (inputSchema.required as unknown[]).filter((r): r is string => typeof r === 'string')
    : [];
  const requiredSet = new Set(requiredList);

  const params: CdaParam[] = Object.entries(properties).map(([name, raw]) => {
    const p = asRecord(raw) || {};
    return {
      name,
      type: typeof p.type === 'string' ? p.type : undefined,
      required: requiredSet.has(name),
      description: typeof p.description === 'string' ? p.description : undefined,
      'x-default': p['x-default'],
      default: p.default,
      'x-normalize': typeof p['x-normalize'] === 'string' ? (p['x-normalize'] as string) : undefined,
      'x-redact': p['x-redact'] === true,
      'x-elicit': typeof p['x-elicit'] === 'string' ? (p['x-elicit'] as string) : undefined,
      'x-suggest': p['x-suggest'],
      'x-compose': typeof p['x-compose'] === 'string' ? (p['x-compose'] as string) : undefined,
    };
  });

  const impactRaw = asRecord(mcp['x-impact']);
  const impact: CdaImpact | undefined = impactRaw
    ? {
        type: typeof impactRaw.type === 'string' ? (impactRaw.type as CdaImpactType) : undefined,
        description: typeof impactRaw.description === 'string' ? (impactRaw.description as string) : undefined,
      }
    : undefined;

  const scope = asRecord(mcp['x-confirm-scope']) as CdaConfirmScope | null;

  return {
    toolName,
    'x-cda': mcp['x-cda'] === true,
    'x-risk': typeof mcp['x-risk'] === 'string' ? (mcp['x-risk'] as string) : undefined,
    'x-impact': impact,
    'x-confirm-message': typeof mcp['x-confirm-message'] === 'string' ? (mcp['x-confirm-message'] as string) : undefined,
    'x-confirm-scope': scope || undefined,
    'x-discloses-value': mcp['x-discloses-value'] === true,
    'x-evidence-source': asRecord(mcp['x-evidence-source']) || undefined,
    'x-operation-ref': typeof mcp['x-operation-ref'] === 'string' ? (mcp['x-operation-ref'] as string) : undefined,
    'x-delegation': mcp['x-delegation'],
    params,
  };
}
